import PublicAdminPanelInstance from "../api/AffiliatePanelInstance";
import { API_ROUTES } from "../api/ApiRoutes";
import { setAppConfig } from "../store/slice/configSlice";
import { applyResolvedI18nLanguage } from "../utils/i18n/languagePreference";
import { mergeV2Localization } from "../utils/i18n/platformConfig";
import {
  AFFILIATE_PORTAL_LOGIN_TIME_KEY,
  AFFILIATE_PORTAL_TOKEN_KEY,
  clearRegulatorPortalSession,
  decodeIdTokenClaims,
  isAffiliatesGroupMember,
  setAffiliatePortalToken,
  setRegulatorPortalToken,
} from "../utils/tokenUtils";
import { fetchRegulatorMe } from "../queries/regulator-portal/regulatorMeClient";
import FeatureFlagService from "./FeatureFlagService";
import { isIpWhitelistBlockedError } from "./ipWhitelistAccessService";
import { AUTH_AUDIENCES } from "../utils/auth/authAudience";
import { AFFILIATE_PORTAL_DASHBOARD_ROUTE } from "../routing/affiliatePortalRoutes";
import { REGULATOR_PORTAL_DASHBOARD_ROUTE } from "../components/RegulatorPortal/regulatorPortalRoutes";

export const AUTH_AUDIENCE_MISMATCH_ERROR_CODE = "AUTH_AUDIENCE_MISMATCH";

const createAuthAudienceMismatchError = () => {
  const error = new Error(AUTH_AUDIENCE_MISMATCH_ERROR_CODE);
  error.code = AUTH_AUDIENCE_MISMATCH_ERROR_CODE;
  return error;
};

const fetchPlatformConfiguration = () =>
  Promise.all([
    PublicAdminPanelInstance.get(API_ROUTES.GET_PLATFORM_CONFIG),
    PublicAdminPanelInstance.get(API_ROUTES.GET_PLATFORM_CONFIG_V2).catch(
      (error) => {
        if (isIpWhitelistBlockedError(error)) throw error;
        console.error("Error fetching Trivelta Products v2:", error);
        return null;
      },
    ),
  ]);

const applyPlatformConfiguration = async (
  dispatch,
  [appConfigResponse, productsConfigResponse],
) => {
  const platformConfigV2 = productsConfigResponse?.data?.data;
  const appConfig = mergeV2Localization(
    appConfigResponse?.data?.data,
    platformConfigV2,
  );

  dispatch(setAppConfig(appConfig));
  FeatureFlagService.hydrateFromPlatformConfig(
    appConfig?.features_status_config,
  );
  FeatureFlagService.hydrateProductsFromV2(
    platformConfigV2?.products,
  );
  await applyResolvedI18nLanguage(appConfig);
};

export const finalizeAuthenticatedSession = async ({
  token,
  email,
  setToken,
  setLastLogInTimestamp,
  dispatch,
  navigate,
  onIpBlocked,
  audience = AUTH_AUDIENCES.STAFF,
}) => {
  const claims = decodeIdTokenClaims(token.IdToken);
  const isAffiliate = isAffiliatesGroupMember(claims);

  if (audience === AUTH_AUDIENCES.AFFILIATE) {
    if (!isAffiliate) {
      localStorage.removeItem(AFFILIATE_PORTAL_TOKEN_KEY);
      localStorage.removeItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY);
      throw createAuthAudienceMismatchError();
    }
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("lastLogInTime");
    clearRegulatorPortalSession();
    setAffiliatePortalToken(token);
    navigate(AFFILIATE_PORTAL_DASHBOARD_ROUTE, { replace: true });
    return;
  }

  if (isAffiliate) {
    if (audience === AUTH_AUDIENCES.REGULATOR) {
      clearRegulatorPortalSession();
    }
    throw createAuthAudienceMismatchError();
  }

  // Regulators are resolved from the authenticated /me response, not from
  // cognito:groups: a regulator awaiting approval is issued tokens with no
  // group claim, so a claims fork would drop them on the operator UI.
  const regulatorMePromise = fetchRegulatorMe(token?.AccessToken).catch(
    (error) => {
      console.error("Error fetching regulator /me:", error);
      return null;
    },
  );
  const platformConfigurationPromise =
    audience === AUTH_AUDIENCES.STAFF
      ? fetchPlatformConfiguration()
      : undefined;
  // Prevent an unhandled rejection while /me is in flight; staff still awaits
  // the original promise and logs GET_PLATFORM_CONFIG failures there.
  platformConfigurationPromise?.catch(() => {});

  const regulatorMe = await regulatorMePromise;
  if (regulatorMe?.isRegulator) {
    if (audience !== AUTH_AUDIENCES.REGULATOR) {
      throw createAuthAudienceMismatchError();
    }
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("lastLogInTime");
    localStorage.removeItem(AFFILIATE_PORTAL_TOKEN_KEY);
    localStorage.removeItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY);
    setRegulatorPortalToken(token);
    navigate(REGULATOR_PORTAL_DASHBOARD_ROUTE, { replace: true });
    return;
  }

  if (audience !== AUTH_AUDIENCES.STAFF) {
    clearRegulatorPortalSession();
    throw createAuthAudienceMismatchError();
  }

  localStorage.removeItem(AFFILIATE_PORTAL_TOKEN_KEY);
  localStorage.removeItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY);
  clearRegulatorPortalSession();

  const loginTime = new Date();
  setToken(token);
  setLastLogInTimestamp(loginTime);
  localStorage.setItem("token", JSON.stringify(token));
  localStorage.setItem("email", JSON.stringify(email));
  localStorage.setItem("lastLogInTime", loginTime.toISOString());

  try {
    await applyPlatformConfiguration(
      dispatch,
      await platformConfigurationPromise,
    );
  } catch (error) {
    if (isIpWhitelistBlockedError(error)) {
      localStorage.removeItem("token");
      localStorage.removeItem("email");
      localStorage.removeItem("lastLogInTime");
      setToken?.({});
      setLastLogInTimestamp?.(undefined);
      onIpBlocked?.();
      return;
    }
    console.error("error fetching app config", error);
  }
};
